"use client";

import { useEffect, useRef, useState } from "react";
import Loading from "@/app/components/Loading";
import Gate, { GateHold, useGateHold } from "@/app/components/Gate";
import { ViewSetting } from "./PlaySettings";

const WORLD = "Baunheim";

type Kind = "scenes" | "hours" | "trades" | "battles" | "crafts" | "growth" | "inputs" | "reports";

interface Scene {
  no: string;
  range: string;
  paragraphs: string[];
}
interface HourFile {
  no: string;
  range: string;
  lines: { time: string; text: string }[];
}
interface Trade {
  time: string;
  change: string;
  text: string;
}
interface Battle {
  id: string;
  time: string;
  result: string;
  turns: number;
  enemies: string[];
  turns_log: { turn: number; lines: string[] }[];
}
interface Input {
  key?: string;
  time: string;
  amplify: string;
  text: string;
}
interface Growth {
  time: string;
  kind: "gain" | "spend";
  text: string;
  change: string;
}
interface Craft {
  id: string;
  time: string;
  target: string;
  count: number;
  result: string;
  hands: { stem: string; text: string; feel: string }[];
}

/** 受け口の log が読む種類。ほかは未実装 */
const READY = new Set<Kind>(["scenes", "hours", "trades", "battles", "crafts", "growth", "inputs"]);

const TABS: { kind: Kind; label: string }[] = [
  { kind: "scenes", label: "場面" },
  { kind: "hours", label: "時間" },
  { kind: "trades", label: "取引" },
  { kind: "battles", label: "戦闘" },
  { kind: "crafts", label: "作製" },
  { kind: "growth", label: "成長" },
  { kind: "inputs", label: "入力" },
  { kind: "reports", label: "報告" },
];

const EMPTY: Record<Kind, string> = {
  scenes: "まだ場面はありません。",
  hours: "まだ時間の記録はありません。",
  trades: "まだ物と金の出入りはありません。",
  battles: "まだ戦闘はありません。",
  crafts: "まだ作製はありません。",
  growth: "まだ成長はありません。",
  inputs: "まだ確定した入力はありません。",
  reports: "",
};

/**
 * ログの画面。場面・時間・取引・戦闘・作製・成長・入力・報告 の 8 つのタブで組む。
 * 場面の散文は、設定画面で選んだ文字サイズと表示で出す。
 * 時刻は共通時間で出す。時間は古いものから下へ並べ、ほかは新しいものを上に並べる。
 * 中身の定めは Baunheim の WebUI の詳細設計書が持つ
 */
export default function PlayLog({ region, slug, view, version }: { region: string; slug: string; view: ViewSetting; /** 進行が動いた印。変わったら読み直す */ version?: string }) {
  const [kind, setKind] = useState<Kind>("scenes");
  // 読んだ中身は種類を添えて持つ。タブを替えた直後に前の種類の中身を描かない
  const [loaded, setLoaded] = useState<{ kind: Kind; entries: unknown[] } | null>(null);
  const entries = loaded && loaded.kind === kind ? loaded.entries : null;
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    setLoaded(null);
    setMessage("");
    setOpen(null);
    if (!READY.has(kind)) return;
    let alive = true;
    fetch(`/api/play/log?world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(slug)}&kind=${kind}`)
      .then(async (response) => {
        const body = await response.json();
        if (!alive) return;
        if (response.ok) setLoaded({ kind, entries: (body.entries ?? []) as unknown[] });
        else setMessage(body.error ?? "ログを読めません");
      })
      .catch(() => alive && setMessage("サーバーに繋がりません"));
    return () => {
      alive = false;
    };
  }, [kind, region, slug]);

  // 進行が動いたら、出ている中身と開いている 1 件を残したまま裏で読み直す
  const firstVersion = useRef(version);
  const kindRef = useRef(kind);
  kindRef.current = kind;
  useEffect(() => {
    if (version === firstVersion.current || !READY.has(kind)) return;
    firstVersion.current = version;
    let alive = true;
    fetch(`/api/play/log?world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(slug)}&kind=${kind}`)
      .then(async (response) => {
        const body = await response.json();
        // 読んでいる間に種類を替えていたら捨てる
        if (alive && response.ok && kindRef.current === kind) setLoaded({ kind, entries: (body.entries ?? []) as unknown[] });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  // 中身を読んでいる最中。ログの画面を開いたときは外のロード画面に、種類を替えたときは中のロード画面に待ってもらう
  const waiting = READY.has(kind) && entries === null && message === "";
  useGateHold(waiting);

  let body: React.ReactNode;
  if (!READY.has(kind)) {
    body = (
      <div className="pl-empty">
        <strong>この記録は未実装です。</strong>
        設計が決まり次第、ここに中身が入ります。
      </div>
    );
  } else if (message) {
    body = <p className="pl-message">{message}</p>;
  } else if (entries === null) {
    body = <Loading />;
  } else if (entries.length === 0) {
    body = <div className="pl-empty">{EMPTY[kind]}</div>;
  } else if (kind === "scenes") {
    body = (
      <div className="pl-list">
        {[...(entries as Scene[])].reverse().map((scene) => (
          <div key={scene.no} className="pl-card pl-log-card">
            <button type="button" className="pl-log-head" aria-expanded={open === scene.no} onClick={() => setOpen(open === scene.no ? null : scene.no)}>
              <span>
                <b>場面 {Number(scene.no)}</b>
                <small>{scene.range}</small>
              </span>
              <span className="pl-log-arrow">{open === scene.no ? "▲" : "▼"}</span>
            </button>
            {open === scene.no ? (
              <div className={`pl-log-body pl-log-scene pl-look-${view.look} pl-size-${view.size}`}>
                <div className="pl-story">
                  {scene.paragraphs.map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
                </div>
              </div>
            ) : (
              <p className="pl-log-peek">{scene.paragraphs[0] ?? ""}</p>
            )}
          </div>
        ))}
      </div>
    );
  } else if (kind === "hours") {
    body = (
      <div className="pl-list">
        {(entries as HourFile[]).map((file, index, all) => {
          const shown = open === file.no || (open === null && index === all.length - 1);
          return (
            <div key={file.no} className="pl-card pl-log-card">
              <button type="button" className="pl-log-head" aria-expanded={shown} onClick={() => setOpen(shown ? "" : file.no)}>
                <span>
                  <b>{file.range}</b>
                  <small>{file.lines.length} 時間</small>
                </span>
                <span className="pl-log-arrow">{shown ? "▲" : "▼"}</span>
              </button>
              {shown ? (
                <ul className="pl-log-rows">
                  {file.lines.map((line) => (
                    <li key={line.time}>
                      <span className="pl-log-time">{line.time}</span>
                      <span>{line.text}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  } else if (kind === "trades") {
    body = (
      <ul className="pl-log-rows pl-log-flat">
        {[...(entries as Trade[])].reverse().map((row, index) => (
          <li key={index}>
            <span className="pl-log-time">{row.time}</span>
            <span>
              {row.text}
              <small className="pl-log-change">{row.change}</small>
            </span>
          </li>
        ))}
      </ul>
    );
  } else if (kind === "growth") {
    body = (
      <ul className="pl-log-rows pl-log-flat">
        {[...(entries as Growth[])].reverse().map((row, index) => (
          <li key={index}>
            <span className="pl-log-time">{row.time}</span>
            <span>
              {row.text}
              <small className="pl-log-change">{row.change}</small>
            </span>
          </li>
        ))}
      </ul>
    );
  } else if (kind === "battles") {
    body = (
      <div className="pl-list">
        {[...(entries as Battle[])].reverse().map((one) => (
          <div key={one.id} className="pl-card pl-log-card">
            <button type="button" className="pl-log-head" aria-expanded={open === one.id} onClick={() => setOpen(open === one.id ? null : one.id)}>
              <span>
                <b>{one.enemies.join("・")}</b>
                <small>
                  {one.time} ・ {one.result}（{one.turns} ターン）
                </small>
              </span>
              <span className="pl-log-arrow">{open === one.id ? "▲" : "▼"}</span>
            </button>
            {open === one.id ? (
              <div className="pl-log-body">
                {one.turns_log.map((t) => (
                  <div key={t.turn}>
                    {t.turn > 0 ? <p className="pl-card-line">ターン {t.turn}</p> : null}
                    <ul className="pl-log-rows pl-log-plain">
                      {t.lines.map((line, index) => (
                        <li key={index}>
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    );
  } else if (kind === "crafts") {
    body = (
      <div className="pl-list">
        {[...(entries as Craft[])].reverse().map((one) => (
          <div key={one.id} className="pl-card pl-log-card">
            <button type="button" className="pl-log-head" aria-expanded={open === one.id} onClick={() => setOpen(open === one.id ? null : one.id)}>
              <span>
                <b>
                  {one.target}
                  {one.count > 1 ? ` ${one.count}` : ""}
                </b>
                <small>
                  {one.time} ・ {one.result}
                </small>
              </span>
              <span className="pl-log-arrow">{open === one.id ? "▲" : "▼"}</span>
            </button>
            {open === one.id ? (
              <ul className="pl-log-rows">
                {one.hands.map((hand, index) => (
                  <li key={index}>
                    <span className="pl-log-time">{index + 1} 手目</span>
                    <span>
                      {hand.stem} {hand.text}
                      <small className="pl-log-change">{hand.feel}</small>
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    );
  } else {
    body = (
      <div className="pl-list">
        {[...(entries as Input[])].reverse().map((row) => (
          <div key={row.key ?? row.time} className="pl-card">
            <p className="pl-card-line">
              {row.time} ・ おまかせ：{row.amplify}
            </p>
            <p className="pl-log-input">{row.text}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="pl-tabbed">
      <div className="pl-tabs pl-tabs-folder" role="tablist" aria-label="ログの種類">
        {TABS.map((one) => (
          <button
            key={one.kind}
            type="button"
            role="tab"
            aria-selected={kind === one.kind}
            className={kind === one.kind ? "pl-tab pl-tab-on" : "pl-tab"}
            onClick={() => setKind(one.kind)}
          >
            {one.label}
          </button>
        ))}
      </div>
      <div className="pl-folder-body">
        {/* 種類を替えるたびに、その種類の中身が出せるまでロード画面を挟む */}
        <Gate key={kind} variant="inline">
          <GateHold busy={waiting} />
          {body}
        </Gate>
      </div>
    </div>
  );
}
